import { useEffect, useRef } from 'react';

import { useLeafletMap } from '@/lib/leaflet/useLeafletMap';
import type { TileLayer } from '@/lib/leaflet/leafletMap';
import type { GeoPoint } from '@/signalr/contracts';
import type { MapMarker } from './markers';

export type LeafletMapViewProps = {
  center?: GeoPoint | null;
  zoom?: number;
  markers?: readonly MapMarker[];
  disableControls?: boolean;
  tileLayers: TileLayer[];
  onMarkerClicked?: (markerId: string) => void | Promise<void>;
};

const DEFAULT_ZOOM = 13;

export function LeafletMapView({
  center,
  zoom,
  markers = [],
  disableControls = false,
  tileLayers,
  onMarkerClicked,
}: LeafletMapViewProps) {
  const onMarkerClickedRef = useRef(onMarkerClicked);
  onMarkerClickedRef.current = onMarkerClicked;

  const { containerRef, map } = useLeafletMap({
    markerClicked: (markerId) => void onMarkerClickedRef.current?.(markerId),
    contextMenuItemClicked: () => {},
  });

  const latitude = center?.latitude;
  const longitude = center?.longitude;
  useEffect(() => {
    if (map == null || latitude == null || longitude == null) {
      return;
    }

    map.setView([latitude, longitude], zoom ?? DEFAULT_ZOOM);
  }, [map, latitude, longitude, zoom]);

  useEffect(() => {
    if (map == null) {
      return;
    }

    map.clearMarkers();
    for (const marker of markers) {
      map.addMarker(
        marker.markerId,
        marker.latLng,
        marker.content,
        marker.markerIconUrl,
        marker.iconSize,
        marker.iconAnchor,
        marker.popupAnchor,
      );
    }
  }, [map, markers]);

  const lastDisableControlsRef = useRef<{ map: unknown; value: boolean }>({ map: undefined, value: false });
  useEffect(() => {
    if (map == null) {
      return;
    }

    if (lastDisableControlsRef.current.map !== map) {
      lastDisableControlsRef.current = { map, value: false };
    }

    if (disableControls !== lastDisableControlsRef.current.value) {
      map.setDisableControls(disableControls);
      lastDisableControlsRef.current.value = disableControls;
    }
  }, [map, disableControls]);

  useEffect(() => {
    if (map == null) {
      return;
    }

    map.setTileLayers(tileLayers);
  }, [map, tileLayers]);

  return <div id="map" ref={containerRef} className="leaflet-map z-1" style={{ width: '100%', height: '100%' }}></div>;
}
